<template>
	<div id="calc-8" class="calc-flex-col card calc-style">
		<div class="card-header">
			<h4 class="calc-text-center">Flux to Flow Rate Calculator</h4>
		</div>
		<div class="card-body">
			<div v-if="errored">
				<p>
					We're sorry, it appears we're unable to retrieve this
					information at the moment. Please try back later.
				</p>
			</div>
			<form
				v-else
				ref="calcForm"
				class="calc-form"
				@submit.prevent="onSubmit"
			>
				<loading
					:active="isLoading"
					:is-full-page="loaderSettings.fullPage"
					:height="loaderSettings.height"
					:width="loaderSettings.width"
					:color="loaderSettings.color"
					:loader="loaderSettings.loader"
					:blur="loaderSettings.blur"
				/>
				<div class="calc-flex-col calc-pb-2">
					<label for=""
						>Product Number
						<small
							><i
								>Continue typing to filter part numbers.</i
							></small
						></label
					>
					<div class="calc-flex-row">
						<SearchAutocomplete
							:key="componentKey"
							v-model="form.partNumber"
							:product-data="productData"
							@report-part-number="getPartNumber"
						/>
					</div>
				</div>
				<div class="calc-flex-col calc-pb-2">
					<label for=""
						>Membrane Area <span>(m<sup>2</sup>)</span>
					</label>
					<input
						v-model="form.membraneArea"
						type="text"
						class="disabled"
						disabled
						@keyup.enter="submitForm"
					/>
					<span
						v-if="v$.form.membraneArea.$error"
						class="error calc-py-1"
						>{{ v$.form.membraneArea.$errors[0].$message }}</span
					>
				</div>
				<div class="calc-flex-col calc-pb-2">
					<label for=""
						>Flux (LMH)
						<span class="calc-required-field">Required</span></label
					>
					<input
						v-model="form.fluxEst"
						type="text"
						@keyup.enter="submitForm"
					/>
					<span
						v-if="v$.form.fluxEst.$error"
						class="error calc-py-1"
						>{{ v$.form.fluxEst.$errors[0].$message }}</span
					>
				</div>
				<div
					class="calc-flex calc-flex-sm-col--rev calc-flex-justify-center calc-flex-sm-align-items"
				>
					<button
						type="button"
						class="reset-btn btns calc-my-2"
						@click="resetForm"
					>
						RESET
					</button>
					<button
						type="button"
						class="submit-btn btns calc-my-2"
						@click="submitForm"
					>
						CALCULATE
					</button>
				</div>
			</form>

			<div class="calc-output">
				<h4>Calculation Results</h4>

				<table
					class="border-collapse table-auto w-full calc-table-striped"
				>
					<thead>
						<tr class="calc-bg-gray text-white">
							<th class="calc-p-4 calc-pl-8 calc-pb-3 text-left">
								Properties
							</th>
							<th class="calc-p-4 calc-pb-3 text-left">Values</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td class="border-b calc-p-2 calc-pl-8">
								<p>Avg Flow Rate (L/min)</p>
							</td>
							<td class="border-b calc-p-4 calc-pb-3">
								<p>
									{{ flowRate }}
								</p>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, numeric } from "@vuelidate/validators";
import SearchAutocomplete from "./SearchAutocomplete.vue";
import Loading from "vue-loading-overlay";

export default {
	name: "CalcEight",
	components: {
		SearchAutocomplete,
		Loading,
	},
	props: {
		loaderSettings: Object,
		productData: Array,
	},
	setup() {
		return { v$: useVuelidate() };
	},
	data: () => ({
		form: {
			partNumber: "",
			membraneArea: "",
			fluxEst: "",
		},
		partNumber: "",
		flowRate: "",
		valid: false,
		componentKey: 0,
		isLoading: 0,
		errored: false,
	}),
	watch: {
		isLoading(value) {
			this.setLoading(value);
		},
	},
	methods: {
		getPartNumber(value) {
			this.form.partNumber = value;
			this.autopopulateFields(this.form.partNumber);
		},
		autopopulateFields(partNumber) {
			const validNumberFormat = /(-\d+|\d+)(,\d+)*(\.\d+)*/g;

			this.setLoading(true);

			if (!partNumber.match(validNumberFormat)) {
				console.error("Not a valid match");
				return;
			}

			if (typeof partNumber === "undefined" || !partNumber.length > 0) {
				console.error(`Part number is ${partNumber}`);
				return;
			}

			// Loop through productData object and push each product into the selectedProductArr
			for (const product of Object.entries(this.productData)) {
				// If the inputted part number matches the current part number
				if (
					product[1].Part_Number.toLowerCase() ===
					partNumber.toLowerCase()
				) {
					let convertedMembraneArea = Number.parseFloat(
						product[1].SA_CM2.match(validNumberFormat)
					);

					// Convert membraneArea to M^2
					this.form.membraneArea = convertedMembraneArea / 10000;
				}
			}

			this.setLoading(false);
		},
		submitForm() {
			this.setLoading(true);
			this.onSubmit();
		},
		onSubmit() {
			this.v$.$validate();
			if (!this.v$.$error) {
				this.isValid();

				let flowRateVal =
					(this.form.fluxEst * this.form.membraneArea * 1000) /
					60 /
					1000; // H63=E63*D63*1000/60

				this.flowRate = flowRateVal.toFixed(8);
			}

			this.setLoading(false);
		},
		resetForm() {
			// Reset validation dirty states
			this.v$.$reset();

			// Clear data
			const initialData = this.$options.data.call(this);
			Object.assign(this.$data, initialData);

			// Remount component
			this.$nextTick(function () {
				// Force component to rerender.
				this.componentKey += 1;
			});
		},
		isValid() {
			this.valid = true;
		},
		setLoading(value) {
			this.handleLoadingChange(value);
		},
		handleLoadingChange(loading) {
			// Only show the loading overlay if it's not already showing
			if (loading === 1 || loading === true) {
				this.loader = this.$loading.show({
					isFullPage: false,
					color: this.loaderSettings.color,
					blur: this.loaderSettings.blur,
					container: this.$refs.formContainer,
				});
			} else {
				if (this.loader) {
					this.loader.hide();
				}
			}
		},
	},
	validations() {
		return {
			form: {
				membraneArea: { required, numeric }, // D63
				fluxEst: { required, numeric }, // E63
			},
		};
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
